import { useState } from "react";

function ProjectsCard({ project, index }) {
  const projectId = `project-${index}`;
  const [isExpanded, setIsExpanded] = useState(false);
  const characterLimit = 200;

  const shouldTruncate = project.summary.length > characterLimit;
  const displayText =
    isExpanded || !shouldTruncate
      ? project.summary
      : project.summary.slice(0, characterLimit) + "...";

  return (
    <div
      id={projectId}
      className="p-6 border border-black/10 rounded-lg bg-white/50 hover:bg-white/80 hover:border-black/20 transition-all flex flex-col scroll-mt-20"
    >
      <div className="mb-4">
        <h3 className="font-display text-2xl font-bold text-hero-foreground mb-1">
          {project.title}
        </h3>
        <h4 className="font-body text-lg font-semibold text-blue-600">
          {project.subtitle}
        </h4>
      </div>

      <div className="mb-6">
        <p className="font-body text-hero-foreground/80 leading-relaxed flex-grow">
          {displayText}
        </p>
        {shouldTruncate && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-blue-600 hover:text-blue-700 font-semibold text-sm transition-colors"
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      <div className="mb-6">
        <h5 className="font-display font-bold text-hero-foreground mb-3">
          Technologies Used
        </h5>
        <ul className="flex flex-wrap gap-2">
          {project.technologies.map((tech, index) => (
            <li
              key={index}
              className="inline-block px-3 py-1 bg-blue-600/10 border border-blue-600/20 rounded-full text-sm font-body text-blue-600"
            >
              {tech}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 pt-6 border-t border-black/10 flex items-center gap-4">
        {/* Live Demo Link */}
        {project.liveDemo && (
          <a
            href={project.liveDemo}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600/10 hover:bg-blue-600/20 transition-colors group"
            title="Live Demo"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </a>
        )}

        {/* Docs/Executive Summary */}
        {project.executiveSummary && (
          <a
            href={project.executiveSummary}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600/10 hover:bg-blue-600/20 transition-colors group"
            title="Executive Summary"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19 2H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3zm-5 18H5a1 1 0 0 1-1-1v-1h10v2zm0-4H4V4a1 1 0 0 1 1-1h10v11zm4 4h-2v-2h2v2zm0-4h-2V4h2v12z" />
            </svg>
          </a>
        )}

        {/* GitHub Repository */}
        {project.githubRepository && (
          <a
            href={project.githubRepository}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-900/10 hover:bg-gray-900/20 transition-colors group"
            title="GitHub Repository"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 text-gray-900 group-hover:scale-110 transition-transform"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57C20.565 21.795 24 17.31 24 12c0-6.63-5.37-12-12-12z" />
            </svg>
          </a>
        )}

        {/* System Architecture Diagram */}
        {project.systemArchitectureLink && (
          <a
            href={project.systemArchitectureLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-purple-600/10 hover:bg-purple-600/20 transition-colors group"
            title="System Architecture Diagram"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 text-purple-600 group-hover:scale-110 transition-transform"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <line x1="10" y1="6.5" x2="14" y2="6.5" />
              <line x1="10" y1="17.5" x2="14" y2="17.5" />
              <line x1="6.5" y1="10" x2="6.5" y2="14" />
              <line x1="17.5" y1="10" x2="17.5" y2="14" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}

export default ProjectsCard;
